import { useMemo } from 'react';

/**
 * Lekki renderer markdown — bez zewn. zależności. Obsługuje:
 *  - # / ## / ### nagłówki
 *  - **bold**, *italic*
 *  - listy "- " i "1. "
 *  - akapity (puste linie)
 *  - inline `kod`
 *
 * To NIE jest kompletny parser CommonMark — wystarczający do wyświetlania umów AI.
 */

interface Props {
  source: string;
  className?: string;
}

type Block =
  | { kind: 'h1' | 'h2' | 'h3'; text: string }
  | { kind: 'p'; text: string }
  | { kind: 'ul' | 'ol'; items: string[] };

function parseBlocks(md: string): Block[] {
  const blocks: Block[] = [];
  const lines = md.split(/\r?\n/);
  let paragraph: string[] = [];
  let listKind: 'ul' | 'ol' | null = null;
  let listItems: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    blocks.push({ kind: 'p', text: paragraph.join(' ').trim() });
    paragraph = [];
  };
  const flushList = () => {
    if (listKind && listItems.length > 0) {
      blocks.push({ kind: listKind, items: listItems });
    }
    listKind = null;
    listItems = [];
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (line.length === 0) {
      flushParagraph();
      flushList();
      continue;
    }
    if (line.startsWith('### ')) {
      flushParagraph(); flushList();
      blocks.push({ kind: 'h3', text: line.slice(4) });
    } else if (line.startsWith('## ')) {
      flushParagraph(); flushList();
      blocks.push({ kind: 'h2', text: line.slice(3) });
    } else if (line.startsWith('# ')) {
      flushParagraph(); flushList();
      blocks.push({ kind: 'h1', text: line.slice(2) });
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      flushParagraph();
      if (listKind !== 'ul') { flushList(); listKind = 'ul'; }
      listItems.push(line.slice(2));
    } else if (/^\d+\.\s/.test(line)) {
      flushParagraph();
      if (listKind !== 'ol') { flushList(); listKind = 'ol'; }
      listItems.push(line.replace(/^\d+\.\s/, ''));
    } else {
      flushList();
      paragraph.push(line);
    }
  }
  flushParagraph();
  flushList();
  return blocks;
}

/** Render inline markdown: **bold**, *italic*, `code`. Zwraca tablicę React nodów. */
function renderInline(text: string): (string | JSX.Element)[] {
  const out: (string | JSX.Element)[] = [];
  let i = 0;
  let key = 0;
  while (i < text.length) {
    if (text[i] === '*' && text[i + 1] === '*') {
      const end = text.indexOf('**', i + 2);
      if (end !== -1) {
        out.push(<strong key={key++}>{text.slice(i + 2, end)}</strong>);
        i = end + 2;
        continue;
      }
    }
    if (text[i] === '*') {
      const end = text.indexOf('*', i + 1);
      if (end !== -1) {
        out.push(<em key={key++}>{text.slice(i + 1, end)}</em>);
        i = end + 1;
        continue;
      }
    }
    if (text[i] === '`') {
      const end = text.indexOf('`', i + 1);
      if (end !== -1) {
        out.push(<code key={key++} className="font-mono text-[0.9em] bg-ink-50 px-1 py-0.5 rounded">
          {text.slice(i + 1, end)}
        </code>);
        i = end + 1;
        continue;
      }
    }
    // Zbierz "zwykły" znak. Akumuluje do najbliższego markera.
    let j = i;
    while (j < text.length && text[j] !== '*' && text[j] !== '`') j++;
    out.push(text.slice(i, j));
    i = j;
  }
  return out;
}

/**
 * Konwertuje markdown do HTML stringa — używane przez TiptapEditor (oczekuje HTML jako wartość).
 * Jeśli wejście wygląda już jak HTML (zaczyna się od `<`), zwraca bez zmian.
 */
export function markdownToHtml(md: string): string {
  if (!md) return '';
  const trimmed = md.trim();
  if (trimmed.startsWith('<')) return md;
  const blocks = parseBlocks(md);
  return blocks.map(b => {
    if (b.kind === 'h1') return `<h1>${inlineHtml(b.text)}</h1>`;
    if (b.kind === 'h2') return `<h2>${inlineHtml(b.text)}</h2>`;
    if (b.kind === 'h3') return `<h3>${inlineHtml(b.text)}</h3>`;
    if (b.kind === 'p')  return `<p>${inlineHtml(b.text)}</p>`;
    if (b.kind === 'ul') return `<ul>${b.items.map(i => `<li>${inlineHtml(i)}</li>`).join('')}</ul>`;
    if (b.kind === 'ol') return `<ol>${b.items.map(i => `<li>${inlineHtml(i)}</li>`).join('')}</ol>`;
    return '';
  }).join('');
}

function inlineHtml(text: string): string {
  // Escape HTML entities pierwsze
  let s = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // `code` musi być przed innymi (zawartość code nie powinna być interpretowana)
  s = s.replace(/`([^`]+)`/g, '<code>$1</code>');
  // **bold**
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  // *italic* — uważamy na pojedyncze gwiazdki; nie kolidują już z bold po powyższym
  s = s.replace(/(?<![*\w])\*([^*\n]+)\*(?![*\w])/g, '<em>$1</em>');
  return s;
}

export function MarkdownView({ source, className = '' }: Props) {
  const blocks = useMemo(() => parseBlocks(source ?? ''), [source]);
  return (
    <div className={'markdown-view text-[14px] text-ink-900 leading-relaxed ' + className}>
      {blocks.map((b, i) => {
        if (b.kind === 'h1') return <h1 key={i} className="text-[22px] font-bold mt-5 mb-3 text-ink-900">{renderInline(b.text)}</h1>;
        if (b.kind === 'h2') return <h2 key={i} className="text-[18px] font-bold mt-4 mb-2 text-ink-900">{renderInline(b.text)}</h2>;
        if (b.kind === 'h3') return <h3 key={i} className="text-[15px] font-bold mt-3 mb-1.5 text-ink-800">{renderInline(b.text)}</h3>;
        if (b.kind === 'p') return <p key={i} className="my-2.5 text-justify">{renderInline(b.text)}</p>;
        if (b.kind === 'ul') return (
          <ul key={i} className="list-disc pl-6 my-2 space-y-1">
            {b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
          </ul>
        );
        if (b.kind === 'ol') return (
          <ol key={i} className="list-decimal pl-6 my-2 space-y-1">
            {b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
          </ol>
        );
        return null;
      })}
    </div>
  );
}
